import { Col, Row } from '@orobix/orobix-fe-components'
import { Formik, FormikErrors } from 'formik'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { DatasetSelectField } from '../../components/DatasetSelect'
import ObservationsSelectField from '../../components/ObservationsSelectField/ObservationsSelectField'
import NotificationSettigs from '../../components/Settings/NotificationsSettings/NotificationsSettings'
import { SettingsContext } from '../../components/Settings/SettingsContext'
import useUpdateSettings from '../../components/Settings/useUpdateSettings'
import TrainingInfo from '../../components/TrainingInfo/TrainingInfo'
import TrainingName from '../../components/TrainingName/TrainingName'
import TrainingNotes from '../../components/TrainingNotes/TrainingNotes'
import { createTrainingTest } from '../../store/storeActions'
import { Observation, ProductType } from '../../types'
import TimeBox from '../TrainingNew/TimeBox/TimeBox'
import StartTrainingTestButton from './StartTrainingTestButton/StartTrainingTestButton'
import StartTrainingTestConfirmationModal from './StartTrainingTestConfirmationModal/StartTrainingTestConfirmationModal'
import useStyles from './TrainingTestNewForm.styles'
import { getEstimatedTestDuration } from './utils'

const requiredImages = 1

interface TrainingTestNewFormProps {
  projectId: string
  productId: string
  trainingId: string
  productType: ProductType
}

interface FormValues {
  selectedObservations: Observation[]
  name?: string
  notes?: string
}

const TrainingTestNewForm = ({
  productId,
  projectId,
  trainingId,
  productType,
}: TrainingTestNewFormProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const classes = useStyles()
  const [
    startTrainingTestConfirmationModal,
    setStartTrainingTestConfirmationModal,
  ] = useState(false)
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])

  const initialValues: FormValues = {
    selectedObservations: [],
    name: '',
    notes: '',
  }

  const { settings } = useContext(SettingsContext)
  const { updateSettings, inProgress } = useUpdateSettings()

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      validate={(values) => {
        const errors: FormikErrors<{ [field: string]: string }> = {}

        if (values.selectedObservations.length < requiredImages) {
          errors.selectedObservations = t(
            'You need to use at least {{ count }} image',
            { count: requiredImages }
          )
        }

        return errors
      }}
      onSubmit={(values, { setSubmitting }) => {
        const { selectedObservations } = values

        createTrainingTest(
          Number(projectId),
          Number(productId),
          Number(trainingId),
          selectedObservations.map((o) => o.id),
          setSubmitting,
          history.push,
          values.name,
          values.notes,
          false,
          selectedDatasets.length > 0
            ? selectedDatasets.map((id) => Number(id))
            : undefined
        )
      }}
    >
      {({ values, isSubmitting, isValid, submitForm }) => (
        // cannot use <form> tag here, see https://orobix.atlassian.net/browse/AIG-1101
        <div role="form" className={classes.form}>
          <Row noBottomPadding className={classes.row}>
            <Col xs={8} xl={9} className={classes.col}>
              <ObservationsSelectField
                name="selectedObservations"
                productType={productType}
                productId={Number(productId)}
                trainingId={Number(trainingId)}
                fixedFilters={{ availableForTesting: Number(trainingId) }}
                useAvailabilityFilter
                excludeTrainingObservations
                dataset={
                  selectedDatasets.length > 0 ? selectedDatasets : undefined
                }
                inTraining={false}
                inTest
              />
            </Col>
            <Col xs={4} xl={3}>
              <div id="asideDivId">
                <TrainingInfo
                  selectedObservations={values.selectedObservations}
                  productType={productType}
                  showOnlyTotal={false}
                />
                <DatasetSelectField
                  fieldName="dataset"
                  productId={Number(productId)}
                  onSelectedDatasets={setSelectedDatasets}
                  excludeTrainingId={Number(trainingId)}
                />
                <TrainingName fieldName="name" />
                <TrainingNotes fieldName="notes" />
                <NotificationSettigs
                  isInSettingsPage={false}
                  isLoading={inProgress}
                  settings={settings}
                  updateSettings={updateSettings}
                />
                <StartTrainingTestButton
                  isDisabled={isSubmitting || !isValid}
                  isLoading={isSubmitting}
                  onSkipConfirmation={submitForm}
                  onRequiresConfirmation={() =>
                    setStartTrainingTestConfirmationModal(true)
                  }
                />
                <div>
                  <TimeBox>
                    {isValid
                      ? getEstimatedTestDuration(
                          values.selectedObservations.length
                        )
                      : t(
                          'Fill in all the required fields to see the estimated test duration.'
                        )}
                  </TimeBox>
                </div>
              </div>
            </Col>
          </Row>

          {startTrainingTestConfirmationModal && (
            <StartTrainingTestConfirmationModal
              onCloseRequest={() =>
                setStartTrainingTestConfirmationModal(false)
              }
              onStartTrainingTestRequest={submitForm}
            />
          )}
        </div>
      )}
    </Formik>
  )
}

export default TrainingTestNewForm
